/**
 * Vertical navigation sidebar with slide animation
 * 56px wide, full height below header
 * Slides out when entering workflow/timeline editors
 */

import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sparkles,
  FolderKanban,
  LayoutTemplate,
  MessageSquare,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useNavVisibility } from "./useNavVisibility";
import "./VerticalNav.css";

interface NavItem {
  id: string;
  label: string;
  description: string;
  icon: typeof Sparkles;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: "inspire",
    label: "Inspire",
    description: "Discover content and get inspired",
    icon: Sparkles,
    path: "/inspire",
  },
  {
    id: "studio",
    label: "Studio",
    description: "Manage your productions and workflows",
    icon: FolderKanban,
    path: "/productions",
  },
  {
    id: "templates",
    label: "Templates",
    description: "Browse workflow templates",
    icon: LayoutTemplate,
    path: "/templates",
  },
  {
    id: "chat",
    label: "Chat",
    description: "Your conversations",
    icon: MessageSquare,
    path: "/chat",
  },
];

export interface VerticalNavRef {
  triggerSlideOut: () => Promise<void>;
}

export const VerticalNav = forwardRef<VerticalNavRef>((_, ref) => {
  const location = useLocation();
  const visibility = useNavVisibility();
  const { user } = useAuth();
  // Hide navigation destinations until the user has accepted the Terms of
  // Service (defence-in-depth; the routes themselves are also hard-gated).
  const tosAccepted = user?.hasAcceptedTos !== false;
  const [isSlideInComplete, setIsSlideInComplete] = useState(false);
  const [isSlidingOut, setIsSlidingOut] = useState(false);

  // Trigger slide-in animation on mount when visible
  useEffect(() => {
    if (visibility === "visible" && !isSlidingOut) {
      const timer = setTimeout(() => setIsSlideInComplete(true), 50);
      return () => clearTimeout(timer);
    }
  }, [visibility, isSlidingOut]);

  // Reset states when visibility changes
  useEffect(() => {
    if (visibility === "hidden") {
      setIsSlideInComplete(false);
      setIsSlidingOut(false);
    }
  }, [visibility]);

  // Expose method to trigger slide-out animation
  const triggerSlideOut = useCallback(() => {
    return new Promise<void>((resolve) => {
      setIsSlidingOut(true);
      setTimeout(() => {
        resolve();
      }, 350);
    });
  }, []);

  useImperativeHandle(ref, () => ({
    triggerSlideOut,
  }));

  // Determine nav class based on state
  const getNavClass = () => {
    if (visibility === "hidden" || isSlidingOut) {
      return "vertical-nav--hidden";
    }
    if (isSlideInComplete) {
      return "vertical-nav--visible";
    }
    return "vertical-nav--initial";
  };

  // Check if a nav item is active
  const isActive = (path: string) => {
    if (path === "/productions") {
      // Studio is active for /productions and /productions/:projectId
      return (
        location.pathname === "/productions" ||
        location.pathname.startsWith("/productions/")
      );
    }
    return location.pathname === path;
  };

  const isBinActive = location.pathname === "/bin";

  return (
    <nav className={cn("vertical-nav", getNavClass())}>
      <div className="vertical-nav__items">
        {tosAccepted &&
          NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <NavLink
                key={item.id}
                to={item.path}
                draggable={false}
                className={cn(
                  "vertical-nav__item",
                  active && "vertical-nav__item--active",
                )}
              >
                <Icon className="vertical-nav__icon" />
                <span className="vertical-nav__label">{item.label}</span>
              </NavLink>
            );
          })}
      </div>

      {/* Bottom section with Bin link */}
      {tosAccepted && (
        <div className="vertical-nav__bottom">
          <NavLink
            to="/bin"
            draggable={false}
            className={cn(
              "vertical-nav__item",
              isBinActive && "vertical-nav__item--active",
            )}
          >
            <Trash2 className="vertical-nav__icon" />
            <span className="vertical-nav__label">Bin</span>
          </NavLink>
        </div>
      )}
    </nav>
  );
});

VerticalNav.displayName = "VerticalNav";
